import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import z from 'zod';

import { conversationDetailsQueryOptions, useChatSendMessageMutation } from '@/api/endpoints/conversations';
import { TLConversationType } from '@/api/gen/tessera-backend-api.schemas';
import { useConversationAttachmentCap } from '@/features/conversation/attachment-cap';
import { ConversationContinuationBanner } from '@/features/conversation/ConversationContinuationBanner';
import { ConversationDropZone } from '@/features/conversation/ConversationDropZone';
import { useConversationMessageDraft } from '@/features/conversation/hooks/use-conversation-message-draft';
import { usePendingNewChatSummary } from '@/features/conversation/hooks/use-pending-new-chat-summary';
import { useSelectedModelWireIds } from '@/features/conversation/hooks/use-selected-model-wire-ids';
import { useUploadAttachments } from '@/features/conversation/hooks/use-upload-attachments';
import { ConversationInputNext } from '@/features/conversation/input/ConversationInputNext';
import { ConversationModelSelector } from '@/features/conversation/input/ConversationModelSelector';
import { usePendingNewChatSeedStore } from '@/features/conversation/store/pending-new-chat-seed-store';
import { useSelectedModel } from '@/features/conversation/store/selected-model-store';
import type { ConversationAttachment, ConversationInputSubmission } from '@/features/conversation/types';
import { ConversationLoadingError } from '@/features/conversation/types';
import {
  buildExtraSendContext,
  buildUserMessageFromSendResponse,
  generateConversationId,
} from '@/features/conversation/utils';
import { getAttachmentLimitError } from '@/features/conversation/utils/attachmentLimits';
import { FilePreviewModal } from '@/features/file-preview/FilePreviewModal';
import { toast } from '@/modus/Toast';
import { Wordmark } from '@/modus/Wordmark';
import type { TLAIConversationDetailsResponse } from '@/types/api';

export const Route = createFileRoute('/_protected/t/$tenantId/conversation/new')({
  component: ConversationNewRoute,
  validateSearch: z.object({
    error: z.nativeEnum(ConversationLoadingError).optional().catch(undefined),
  }),
});

function ConversationNewRoute() {
  const { error } = Route.useSearch();
  const { tenantId } = Route.useParams();

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const conversationId = useMemo(() => generateConversationId(), []);
  const selectedModelValue = useSelectedModel(conversationId);
  // This route sends the first message, which is where the pick actually binds.
  const { llmModelId: selectedLlmModelId, llmModelCostId: selectedLlmCostId } =
    useSelectedModelWireIds(selectedModelValue);
  const { draft: messageDraft, setDraft: setMessageDraft } = useConversationMessageDraft({
    conversationId,
    draftStorageKey: 'new',
  });

  const [sendMessage, { isPending: isPendingSend }] = useChatSendMessageMutation();
  const {
    attachments,
    isUploading,
    handleUploadFiles,
    handleAddSystemFile,
    handleRemoveAttachment,
    clearAttachments,
    attachmentError,
    setAttachmentError,
    setAttachments,
  } = useUploadAttachments({
    conversationId,
    draftStorageKey: 'new',
  });

  const [previewAttachment, setPreviewAttachment] = useState<ConversationAttachment | null>(null);
  const attachmentCap = useConversationAttachmentCap();

  // "Summarize & start new chat" hand-off: the user sees only their own query pre-filled below —
  // the carried-over summary rides along invisibly and is attached on the very first send.
  const consumeSeed = usePendingNewChatSeedStore((s) => s.consumeSeed);
  const {
    pendingSummary,
    setPendingSummary,
    scratchpadSourceConversationId,
    setScratchpadSourceConversationId,
    clearPendingSummary,
  } = usePendingNewChatSummary();
  const seedConsumedRef = useRef(false);
  useEffect(() => {
    if (seedConsumedRef.current) return;
    seedConsumedRef.current = true;
    const seed = consumeSeed();
    if (!seed) return;
    // Don't clobber a persisted "new" draft with an empty carry-over (e.g. a queued message that
    // tripped the limit has no query text to seed).
    if (seed.prefillQuery) setMessageDraft(seed.prefillQuery);
    if (seed.attachments.length > 0) setAttachments(seed.attachments);
    if (seed.summary) setPendingSummary(seed.summary);
    setScratchpadSourceConversationId(seed.scratchpadSourceConversationId ?? null);
  }, [consumeSeed, setAttachments, setMessageDraft, setPendingSummary, setScratchpadSourceConversationId]);

  const handleSubmit = useCallback(
    async (submission: ConversationInputSubmission) => {
      if (!submission.input.trim().length) return;

      const attachmentsIds = submission.attachments?.map((attachment) => attachment.program_id);
      const extraContext = buildExtraSendContext({ chatType: TLConversationType.CHAT });

      const result = await sendMessage({
        conversation_id: conversationId,
        message: submission.input,
        chat_type: TLConversationType.CHAT,
        attachments: attachmentsIds?.length ? attachmentsIds : undefined,
        ...(extraContext ? { context: extraContext } : {}),
        ...(submission.invoked_skill_slugs?.length ? { invoked_skill_slugs: submission.invoked_skill_slugs } : {}),
        // Send both during the rollout overlap: a new backend prefers llm_model_id; an old pod that
        // doesn't know the field falls back to the cost id instead of binding the default. The cost
        // id is omitted for an unpriced model (it has none).
        ...(selectedLlmModelId ? { llm_model_id: selectedLlmModelId } : {}),
        ...(selectedLlmCostId ? { llm_model_cost_id: selectedLlmCostId } : {}),
        ...(pendingSummary ? { prior_summary: pendingSummary } : {}),
        ...(scratchpadSourceConversationId
          ? { scratchpad_source_conversation_id: scratchpadSourceConversationId }
          : {}),
      });

      if (result.error) {
        const attachmentLimitMessage = getAttachmentLimitError(result.error, attachmentCap);
        if (attachmentLimitMessage) {
          setAttachmentError(attachmentLimitMessage);
          return;
        }
        toast.error('Failed to send message');
        return;
      }

      const response = result.data;

      // A brand-new conversation cannot normally have an in-flight turn, but keep the union
      // response safe if the server ever resolves this generated ID to an existing conversation.
      if ('queued_message_id' in response) {
        clearAttachments();
        clearPendingSummary();
        navigate({
          to: '/t/$tenantId/conversation/$conversationId',
          params: { tenantId, conversationId: response.conversation_id },
        });
        return;
      }

      queryClient.setQueryData<TLAIConversationDetailsResponse, readonly unknown[], TLAIConversationDetailsResponse>(
        conversationDetailsQueryOptions({ conversation_id: response.conversation_id }).queryKey,
        {
          history: [
            buildUserMessageFromSendResponse(
              response,
              { input: submission.input, attachments: attachmentsIds },
              response.turn_index,
            ),
          ],
          files: submission.attachments ?? [],
          serverTime: new Date().toISOString(),
        },
      );

      clearAttachments();
      clearPendingSummary();
      navigate({
        to: '/t/$tenantId/conversation/$conversationId',
        params: { tenantId, conversationId: response.conversation_id },
      });
    },
    [
      sendMessage,
      conversationId,
      queryClient,
      navigate,
      clearAttachments,
      clearPendingSummary,
      scratchpadSourceConversationId,
      tenantId,
      selectedLlmModelId,
      selectedLlmCostId,
      setAttachmentError,
      pendingSummary,
      attachmentCap,
    ],
  );

  useEffect(() => {
    if (error) {
      toast.error('Failed to load conversation');
    }
  }, [error]);

  return (
    <ConversationDropZone
      className="mx-auto flex size-full min-w-0 max-w-6xl flex-col items-center bg-primary-foreground"
      onUpload={handleUploadFiles}
      disabled={isUploading}
    >
      <div className="flex min-h-0 w-full max-w-3xl flex-1 flex-col items-center justify-end gap-8">
        <Wordmark size={132} orientation="vertical" />
      </div>
      <div className="relative flex w-full max-w-3xl flex-col items-center overflow-visible px-4 pt-8">
        <ConversationContinuationBanner show={!!pendingSummary} className="mb-2 w-full" />
        <ConversationInputNext
          placeholder="Ask a question"
          value={messageDraft}
          onChange={setMessageDraft}
          onSubmit={handleSubmit}
          loading={isPendingSend}
          disabled={isUploading || isPendingSend}
          attachments={attachments}
          attachmentError={attachmentError}
          modelSelector={<ConversationModelSelector conversationId={conversationId} className="shrink-0" />}
          actionsProps={{
            onUpload: handleUploadFiles,
            onSystemFileSelect: handleAddSystemFile,
            selectedFiles: attachments,
            onRemoveAttachment: handleRemoveAttachment,
            onPreviewAttachment: setPreviewAttachment,
          }}
          classes={{
            root: 'relative z-10 overflow-visible rounded-[35px]',
            inputGroup: 'bg-muted/35 backdrop-blur-sm',
          }}
        />
      </div>
      <div className="min-h-0 w-full flex-1" />
      {previewAttachment && (
        <FilePreviewModal
          isOpen={!!previewAttachment}
          programId={previewAttachment.program_id}
          filename={previewAttachment.filename}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </ConversationDropZone>
  );
}
